import { useEffect } from 'react'
import { useAtomValue, useSetAtom, type PrimitiveAtom } from 'jotai'

import { archivedDisplayAtom, pendingDisplayAtom } from '../staged-action-local-state'
import { COMPLETED_ACTION_DURATION_MS } from '../staged-action-status'

/**
 * Garbage-collects elapsed entries from a `Map<id, startMs>` display-window
 * atom. One timer per atom, armed for the nearest deadline, rather than one
 * per row — the sweep re-runs whenever the map changes.
 */
function useDisplayWindowExpiry(displayAtom: PrimitiveAtom<Map<string, number>>): void {
  const display = useAtomValue(displayAtom)
  const setDisplay = useSetAtom(displayAtom)

  useEffect(() => {
    if (display.size === 0) return

    const now = Date.now()
    const expiredIds: Array<string> = []
    let nextDelay: number | null = null

    for (const [id, startMs] of display) {
      const remaining = COMPLETED_ACTION_DURATION_MS - (now - startMs)
      if (remaining <= 0) {
        expiredIds.push(id)
      } else {
        nextDelay = nextDelay == null ? remaining : Math.min(nextDelay, remaining)
      }
    }

    if (expiredIds.length > 0) {
      setDisplay((prev) => {
        let changed = false
        const next = new Map(prev)
        for (const id of expiredIds) {
          changed = next.delete(id) || changed
        }
        return changed ? next : prev
      })
      return
    }

    if (nextDelay == null) return

    const timeout = setTimeout(() => {
      setDisplay((prev) => {
        const nextNow = Date.now()
        let next: Map<string, number> | null = null
        for (const [id, startMs] of prev) {
          if (nextNow - startMs < COMPLETED_ACTION_DURATION_MS) continue
          next ??= new Map(prev)
          next.delete(id)
        }
        return next ?? prev
      })
    }, nextDelay)

    return () => clearTimeout(timeout)
  }, [display, setDisplay])
}

export function usePendingActionExpiry(): void {
  useDisplayWindowExpiry(pendingDisplayAtom)
}

export function useArchivedActionExpiry(): void {
  useDisplayWindowExpiry(archivedDisplayAtom)
}
